import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Linking, Platform, Pressable, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { useTerms } from '@/hooks/use-terms';
import {
  AppleLadderStep,
  TeamPricing,
  TeamSelection,
  capacityForBudget,
  formatCents,
  ladderStepFor,
  quoteTeamPlan,
  roundClients,
} from '@/lib/team-pricing';
import { cn } from '@/utils/cn';

const SUPPORT_WHATSAPP = '5511999999999'; // mesmo número do "Sob medida" dos planos solo
const TABULAR = { fontVariant: ['tabular-nums' as const] };

function StepperRow({
  title,
  value,
  note,
  noteTone = 'muted',
  canDecrease,
  canIncrease,
  onDecrease,
  onIncrease,
  testID,
}: {
  title: string;
  value: string;
  note?: string;
  noteTone?: 'muted' | 'warning';
  canDecrease: boolean;
  canIncrease: boolean;
  onDecrease: () => void;
  onIncrease: () => void;
  testID?: string;
}) {
  return (
    <View className="flex-row items-center gap-3 py-1.5">
      <View className="flex-1 gap-0.5">
        <ThemedText className="font-semibold text-[15px] leading-5 text-neutral-900">
          {title}
        </ThemedText>
        {note ? (
          <ThemedText
            className={cn(
              'text-xs leading-4',
              noteTone === 'warning' ? 'text-amber-700' : 'text-neutral-500',
            )}
          >
            {note}
          </ThemedText>
        ) : null}
      </View>
      <View className="flex-row items-center gap-2">
        <PressableScale
          haptic
          accessibilityRole="button"
          accessibilityLabel={`Diminuir ${title.toLowerCase()}`}
          disabled={!canDecrease}
          onPress={onDecrease}
          testID={testID ? `${testID}-minus` : undefined}
          className="h-10 w-10 items-center justify-center rounded-full bg-neutral-100"
          style={{ opacity: canDecrease ? 1 : 0.4 }}
        >
          <ThemedText className="font-bold text-lg text-neutral-800">−</ThemedText>
        </PressableScale>
        <ThemedText
          className="min-w-12 text-center font-bold text-lg text-neutral-900"
          style={TABULAR}
        >
          {value}
        </ThemedText>
        <PressableScale
          haptic
          accessibilityRole="button"
          accessibilityLabel={`Aumentar ${title.toLowerCase()}`}
          disabled={!canIncrease}
          onPress={onIncrease}
          testID={testID ? `${testID}-plus` : undefined}
          className="h-10 w-10 items-center justify-center rounded-full bg-neutral-100"
          style={{ opacity: canIncrease ? 1 : 0.4 }}
        >
          <ThemedText className="font-bold text-lg text-neutral-800">+</ThemedText>
        </PressableScale>
      </View>
    </View>
  );
}

function QuoteLine({ label, value }: { label: string; value: string }) {
  return (
    <View className="flex-row items-center justify-between gap-3">
      <ThemedText className="flex-1 text-sm text-neutral-600" style={TABULAR}>
        {label}
      </ThemedText>
      <ThemedText className="font-semibold text-sm text-neutral-800" style={TABULAR}>
        {value}
      </ThemedText>
    </View>
  );
}

/**
 * Calculadora do plano de equipe: dois controles (profissionais e clientes por
 * mês) e o preço com a conta aberta embaixo. Começa no uso real. No iPhone,
 * sobe para o degrau da escada Apple e a folga vira clientes.
 */
export function TeamPlanCalculator({
  pricing,
  initial,
  floorProfessionals,
  usedClients,
  store,
  current,
  ctaLabel,
  busy,
  onSubmit,
}: {
  pricing: TeamPricing;
  initial: TeamSelection;
  /** Membros ativos que atendem: abaixo disso a API recusa. */
  floorProfessionals: number;
  /** Clientes atendidos este mês, para o aviso de "abaixo do uso". */
  usedClients: number;
  store: 'stripe' | 'apple';
  /** O que já está contratado, se houver. */
  current?: TeamSelection | null;
  ctaLabel: string;
  busy?: boolean;
  onSubmit: (selection: TeamSelection, appleStep: AppleLadderStep | null) => void;
}) {
  const terms = useTerms();
  const { version, appleLadder } = pricing;
  const minProfessionals = Math.max(version.minProfessionals, floorProfessionals);

  const clampProfessionals = (n: number) =>
    Math.min(Math.max(n, minProfessionals), version.maxProfessionals);

  const [professionals, setProfessionals] = useState(() =>
    clampProfessionals(initial.professionals),
  );
  const [clients, setClients] = useState(() => roundClients(version, initial.clients));

  // Versão nova (ou piso novo) chega depois: re-encaixa sem perder a escolha.
  useEffect(() => {
    setProfessionals((p) => clampProfessionals(p));
    setClients((c) => roundClients(version, c));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version.id, minProfessionals]);

  const quote = useMemo(
    () => quoteTeamPlan(version, { professionals, clients }),
    [version, professionals, clients],
  );

  const appleStep = store === 'apple' ? ladderStepFor(appleLadder, quote.totalCents) : null;
  const appleClients =
    appleStep != null ? capacityForBudget(version, appleStep.priceCents, professionals) : null;

  const perProfessional = Math.round(clients / Math.max(professionals, 1));
  const belowUsage = usedClients > 0 && clients < usedClients;
  const unchanged =
    !!current && current.professionals === professionals && current.clients === clients;
  const atMax = professionals >= version.maxProfessionals || clients >= version.maxClients;
  const appleUnavailable = store === 'apple' && !appleStep;

  const g = (word: string) => terms.agree(word, 'organization');

  const submit = () => {
    if (appleUnavailable) return;
    // No iPhone o degrau compra mais clientes do que o pedido: grava a folga.
    const selection =
      store === 'apple' && appleClients
        ? { professionals, clients: Math.max(clients, appleClients) }
        : { professionals, clients };
    onSubmit(selection, appleStep);
  };

  const talkToUs = () => {
    const msg = `Olá! Tenho uma equipe maior que ${version.maxProfessionals} ${terms.professionalPlural} ou ${version.maxClients} ${terms.clientPlural} por mês e quero um plano sob medida.`;
    const url = `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(msg)}`;
    if (Platform.OS === 'web') window.open(url, '_blank');
    else void Linking.openURL(url);
  };

  return (
    <View
      className="gap-4 rounded-2xl border border-neutral-900/5 bg-card p-4"
      style={{ borderCurve: 'continuous' }}
    >
      {current ? (
        <ThemedText className="text-sm text-neutral-500" style={TABULAR}>
          Contratado: {current.professionals} {terms.professionalPlural}, até {current.clients}{' '}
          {terms.clientPlural}/mês
        </ThemedText>
      ) : null}

      <View className="gap-1">
        <StepperRow
          title={terms.title.professionalPlural}
          value={String(professionals)}
          note={
            floorProfessionals > version.minProfessionals && professionals === minProfessionals
              ? `${floorProfessionals} ativos hoje; desative alguém para diminuir`
              : undefined
          }
          canDecrease={professionals > minProfessionals}
          canIncrease={professionals < version.maxProfessionals}
          onDecrease={() => setProfessionals((p) => clampProfessionals(p - 1))}
          onIncrease={() => setProfessionals((p) => clampProfessionals(p + 1))}
          testID="team-calc-professionals"
        />
        <StepperRow
          title={`${terms.title.clientPlural} por mês`}
          value={String(clients)}
          note={
            belowUsage
              ? `Abaixo dos ${usedClients} atendidos este mês`
              : `≈ ${perProfessional} por ${terms.professional}`
          }
          noteTone={belowUsage ? 'warning' : 'muted'}
          canDecrease={clients > version.blockSize}
          canIncrease={clients < version.maxClients}
          onDecrease={() => setClients((c) => roundClients(version, c - version.blockSize))}
          onIncrease={() => setClients((c) => roundClients(version, c + version.blockSize))}
          testID="team-calc-clients"
        />
      </View>

      <View className="h-px bg-neutral-100" />

      <View className="gap-2">
        <View className="flex-row items-end justify-between gap-3">
          <ThemedText className="font-semibold text-sm text-neutral-700">Total por mês</ThemedText>
          <ThemedText className="font-extrabold text-3xl text-neutral-900" style={TABULAR}>
            {formatCents(quote.totalCents)}
          </ThemedText>
        </View>
        <QuoteLine
          label={`Taxa ${g('do')} ${terms.organization}`}
          value={formatCents(quote.feeCents)}
        />
        <QuoteLine
          label={`${professionals} × ${formatCents(version.seatCents)} por ${terms.professional}`}
          value={formatCents(quote.seatsCents)}
        />
        <QuoteLine
          label={`${quote.blocks} × ${formatCents(version.blockCents)} a cada ${version.blockSize} ${terms.clientPlural}`}
          value={formatCents(quote.blocksCents)}
        />
      </View>

      {store === 'apple' ? (
        <View className="flex-row items-start gap-2 rounded-xl bg-neutral-50 p-3">
          <Ionicons className="text-neutral-600" name="logo-apple" size={16} />
          <ThemedText className="flex-1 text-sm leading-5 text-neutral-600" style={TABULAR}>
            {appleStep && appleClients
              ? `No iPhone: ${formatCents(appleStep.priceCents)}/mês, com ${professionals} ${terms.professionalPlural} e até ${appleClients} ${terms.clientPlural}.`
              : 'Acima do maior plano do iPhone. Fale com a gente.'}
          </ThemedText>
        </View>
      ) : null}

      <PressableScale
        haptic
        onPress={submit}
        disabled={busy || unchanged || appleUnavailable}
        className="h-12 w-full flex-row items-center justify-center rounded-full bg-primary"
        style={{
          borderCurve: 'continuous',
          opacity: busy || unchanged || appleUnavailable ? 0.5 : 1,
        }}
        testID="team-calc-submit"
      >
        {busy ? (
          <ActivityIndicator color="#fff" />
        ) : (
          <ThemedText className="font-bold text-sm text-white">
            {unchanged ? 'Este é o plano atual' : ctaLabel}
          </ThemedText>
        )}
      </PressableScale>

      {/* No iPhone, fechar plano por WhatsApp é compra fora do IAP (3.1.1). */}
      {atMax && store !== 'apple' ? (
        <Pressable onPress={talkToUs} className="items-center py-1">
          <ThemedText className="font-semibold text-sm text-primary">
            Mais que isso? Plano sob medida
          </ThemedText>
        </Pressable>
      ) : null}
    </View>
  );
}
